import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as S from "./styles";
import { Button } from "../../../../components/Button";
import { Logo } from "../../components/Logo";
import { colors } from "../../../../styles/themes/default/foundations/colors";
import useRecoveryPassword from "../../../../hooks/useRecoveryPassword";
import { toastify } from "../../../../shared/Toastify";
import { AxiosError } from "axios";

export function EmailSentSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const countryCode = searchParams.get("countryCode");

  const [secondsLeft, setSecondsLeft] = useState(60);
  const [isLoading, setIsLoading] = useState(false);

  const { resendEmail } = useRecoveryPassword();

  useEffect(() => {
    if (!email || !countryCode) {
      navigate("/forgot-password", { replace: true });
    }
  }, [email, countryCode]);

  useEffect(() => {
    if (secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  async function handleResendEmail() {
    if (!email || !countryCode || secondsLeft > 0) return;

    setIsLoading(true);

    try {
      await resendEmail(email, countryCode);

      setSecondsLeft(60);
    } catch (error) {
      let message = t("failedToResendEmail");

      if (error instanceof AxiosError) {
        message = error?.response?.data?.message || t("failedToResendEmail");
      }

      toastify("error", message, "top-right");
    } finally {
      setIsLoading(false);
    }
  }

  if (!email) {
    return null;
  }

  return (
    <S.EmailSentSuccessContainer>
      <S.ContentContainer>
        <S.IconContainer>
          <Logo />
        </S.IconContainer>

        <S.Description>
          <S.DescriptionEmail>
            <p>{t("passwordRecoveryEmailSent")}</p>
            <S.EmailHighlight>{email}</S.EmailHighlight>
          </S.DescriptionEmail>
          <S.DescriptionLine>
            {t("passwordRecoveryInstructions")}
          </S.DescriptionLine>
          <S.DescriptionLine>{t("linkValidFor10Minutes")}</S.DescriptionLine>
          <S.DescriptionLine>{t("ifYouDontSeeEmail")}</S.DescriptionLine>
          <div>
            <S.DescriptionLine>
              <S.Marker>•</S.Marker>
              {t("doubleCheckEmailAddress")}
            </S.DescriptionLine>
            <S.DescriptionLine>
              <S.Marker>•</S.Marker> {t("checkSpamFolder")}
            </S.DescriptionLine>
            <S.DescriptionLine>
              <S.Marker>•</S.Marker> {t("waitAndResend")}
            </S.DescriptionLine>
          </div>
          <S.DescriptionLine>{t("didntReceiveEmail")}</S.DescriptionLine>
        </S.Description>

        <S.ButtonContainer>
          <Button
            type="button"
            style={{ width: "100%" }}
            disabled={secondsLeft > 0 || isLoading}
            isLoading={isLoading}
            onClick={handleResendEmail}
          >
            <p>
              {secondsLeft > 0
                ? t("resendEmailWithTimer", { seconds: secondsLeft })
                : t("resendEmail")}
            </p>
          </Button>
          <h4>{t("returnToSignIn")}</h4>

          <Button
            type="button"
            onClick={() => navigate(`/signin?email=${email}`)}
            style={{
              width: "100%",
              backgroundColor: colors.white,
              color: colors["black-900"],
            }}
          >
            <p>{t("backToLogin")}</p>
          </Button>
        </S.ButtonContainer>
      </S.ContentContainer>
    </S.EmailSentSuccessContainer>
  );
}
